/**
 * Packaging Add-on: Nested Cart Items
 * 
 * Intercepts add-to-cart submissions and automatically adds selected packaging
 * as a nested child item using Shopify's parent_id feature.
 */

class PackagingCartHandler {
  constructor() {
    this.selectedPackaging = window.selectedPackaging || null;
    this.init();
  }

  init() {
    // Listen for packaging changes
    document.addEventListener('packaging:changed', (e) => {
      this.selectedPackaging = e.detail;
      this.updatePriceDisplay();
    });

    // Listen for cart updates (from product-form.js) to add packaging
    this.subscribeToCartUpdates();
  }

  subscribeToCartUpdates() {
    // Use constant or fallback string
    const eventName = window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update';

    if (window.subscribe) {
      console.log(`PackagingHandler: Subscribing to ${eventName}`);
      
      subscribe(eventName, async (event) => {
        console.log('PackagingHandler: Cart update received', event);
        
        // Refresh selected packaging from global state just in case
        if (window.selectedPackaging) {
          this.selectedPackaging = window.selectedPackaging;
        }

        if (event.source === 'product-form' && this.selectedPackaging?.variantId) {
          console.log('PackagingHandler: Triggering packaging add for', this.selectedPackaging);
          return this.handleCartUpdate(event.cartData, event.productVariantId);
        } else {
          console.log('PackagingHandler: Ignoring update. Source:', event.source, 'VariantId:', this.selectedPackaging?.variantId);
        }
      });
    } else {
      // Retry if PubSub is not ready yet
      console.log('PackagingHandler: PubSub not ready, retrying...');
      setTimeout(() => this.subscribeToCartUpdates(), 100);
    }
  }

  /**
   * Handle cart update event
   */
  async handleCartUpdate(cartData, mainVariantId) {
    // 1. Try to find key directly (standard response)
    let parentKey = cartData?.key;

    // 2. If not found, check if it's inside an 'items' array (drawer/sections response)
    if (!parentKey && cartData?.items && cartData.items.length > 0) {
      // Usually the last item added is the one we want, or the one matching our variant
      const item = cartData.items.find(i => i.variant_id == mainVariantId) || cartData.items[0];
      parentKey = item.key;
      console.log('PackagingHandler: Found key in items array:', parentKey);
    }

    // 3. Fallback: If still missing, try to find it in the cart fetch
    if (!parentKey && mainVariantId) {
      console.log('PackagingHandler: Key missing, searching cart for variant', mainVariantId);
      try {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        // Find the most recent item with this variant ID
        // (We assume it's the first match we find, which is usually correct for simple adds)
        const item = cart.items.find(i => i.variant_id == mainVariantId);
        if (item) {
          parentKey = item.key;
          console.log('PackagingHandler: Found parent key in cart:', parentKey);
        }
      } catch (e) {
        console.error('PackagingHandler: Error fetching cart fallback', e);
      }
    }

    if (!parentKey) {
      console.error('PackagingHandler: Could not find parent item key. Aborting.');
      return;
    }

    try {
      await this.addPackagingItem(parentKey, 1); // Default to 1 for now, or use cartData.quantity
    } catch (error) {
      console.error('Failed to add packaging:', error);
    }
  }

  /**
   * Add packaging item linked to parent
   */
  async addPackagingItem(parentKey, quantity) {
     if (!this.selectedPackaging?.variantId) return;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.selectedPackaging.variantId,
          quantity: quantity,
          properties: {
            '_parent_key': parentKey, // Reference to parent item
            '_is_addon': 'true',
            '_addon_type': 'אריזה',
            '_addon_label': this.selectedPackaging.label
          }
        })
      });
      
      const result = await response.json();

      // Trigger another refresh to show the packaging
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      if (window.publish && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'packaging-handler', 
          cartData: result 
        });
      }
  }


  /**
   * Update displayed price on product page to include packaging
   */
  updatePriceDisplay() {
    const priceElements = document.querySelectorAll('.price__regular .price-item--regular');
    
    if (!priceElements.length) return;

    priceElements.forEach(priceEl => {
      const originalPrice = parseFloat(priceEl.dataset.originalPrice || priceEl.textContent.replace(/[^\d.]/g, ''));
      
      // Store original if not already stored
      if (!priceEl.dataset.originalPrice) {
        priceEl.dataset.originalPrice = originalPrice;
      }

      if (this.selectedPackaging && this.selectedPackaging.price > 0) {
        const newPrice = originalPrice + this.selectedPackaging.price;
        const formattedPrice = this.formatMoney(newPrice * 100); // Convert to cents
        
        // Update display
        if (priceEl.querySelector('.price-item__original')) {
          priceEl.querySelector('.price-item__original').textContent = formattedPrice;
        } else {
          priceEl.textContent = formattedPrice;
        }
      }
    });
  }

  /**
   * Intercept add-to-cart submissions
   */
  interceptAddToCart() {
    // Method 1: Intercept form submissions (for "Add to Cart" button)
    document.addEventListener('submit', (e) => {
      const form = e.target;
      
      // Only intercept cart/add forms, NOT buy-now forms
      if (form.getAttribute('action')?.includes('/cart/add') && 
          !form.querySelector('[name="return_to"]')) {
        e.preventDefault();
        this.handleFormSubmit(form);
      }
    });

    // Method 2: Intercept buy-now button clicks (dynamic checkout)
    this.interceptBuyNowButton();
  }

  /**
   * Handle traditional form submission
   */
  async handleFormSubmit(form) {
    const formData = new FormData(form);
    const mainVariantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;

    // Get all properties from form
    const properties = {};
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.match(/properties\[(.*?)\]/)[1];
        properties[propName] = value;
      }
    }

    await this.addToCartWithPackaging(mainVariantId, quantity, properties);
  }

  /**
   * Add items to cart with packaging as nested item
   */
  async addToCartWithPackaging(mainVariantId, quantity = 1, properties = {}) {
    try {
      // STEP 1: Add main product first
      const mainItemResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: mainVariantId,
          quantity: quantity,
          properties: {
            ...properties,
            '_אריזה': this.selectedPackaging?.label || 'אריזה רגילה'
          }
        })
      });

      if (!mainItemResponse.ok) {
        throw new Error('Failed to add main product to cart');
      }

      const mainItem = await mainItemResponse.json();
      const mainItemKey = mainItem.key; // This is the line item key we need!

      // STEP 2: Add packaging as nested item if selected (and not free option)
      if (this.selectedPackaging?.variantId) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: this.selectedPackaging.variantId,
            quantity: quantity,
            properties: {
              '_parent_key': mainItemKey, // Reference to parent item
              '_is_addon': 'true',
              '_addon_type': 'אריזה',
              '_addon_label': this.selectedPackaging.label
            }
          })
        });
      }

      // Success! Trigger cart update
      this.onAddToCartSuccess(mainItem);
      
      return mainItem;
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.onAddToCartError(error);
      throw error;
    }
  }

  /**
   * Success handler - update cart UI
   */
  onAddToCartSuccess(result) {
    // Trigger native Shopify cart update events
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true
      })
    );

    // Publish to PubSub (Dawn theme standard)
    if (window.publish && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { 
        source: 'packaging-handler', 
        cartData: result 
      });
    } else if (window.Shopify && window.Shopify.PubSub) {
      window.Shopify.PubSub.publish('cart:updated', { cart: result });
    }

    // Open cart drawer if available
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.open) {
      cartDrawer.open();
    } else {
      // Fallback: show notification or redirect to cart
      const notification = document.querySelector('cart-notification');
      if (notification && notification.open) {
        notification.open();
      } else {
        // Last resort: redirect to cart page
        window.location.href = '/cart';
      }
    }
  }

  /**
   * Error handler
   */
  onAddToCartError(error) {
    const errorMessage = error.message || 'שגיאה בהוספה לעגלה';
    alert(errorMessage);
  }

  /**
   * Format money according to shop settings
   */
  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback formatting for ILS
    return '₪' + (cents / 100).toFixed(2);
  }

  /**
   * Intercept Buy Now button to add packaging before checkout
   */
  interceptBuyNowButton() {
    const self = this;

    // Listen for dynamic checkout button clicks
    document.addEventListener('click', async (e) => {
      const buyNowButton = e.target.closest('.shopify-payment-button__button');
      
      if (buyNowButton && self.selectedPackaging?.variantId) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get form data
        const form = buyNowButton.closest('form');
        if (!form) return;
        
        const formData = new FormData(form);
        const mainVariantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        
        // Get properties
        const properties = {};
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propName = key.match(/properties\[(.*?)\]/)[1];
            properties[propName] = value;
          }
        }
        
        try {
          // Add both items to cart first
          await self.addToCartWithPackaging(mainVariantId, quantity, properties);
          
          // Then proceed to checkout
          window.location.href = '/checkout';
        } catch (error) {
          console.error('Error with buy now:', error);
          // If fails, allow original buy now behavior
          buyNowButton.click();
        }
      }
    }, true); // Use capture phase to intercept before Shopify's handler
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.packagingCartHandler = new PackagingCartHandler();
  });
} else {
  window.packagingCartHandler = new PackagingCartHandler();
}
